import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChatCircle, ChatCircleDots, ArrowsClockwise, PaperPlaneTilt,
  X, ArrowLeft, Briefcase
} from '@phosphor-icons/react';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ChatPanel({ partnerId, partnerName, partnerImage, onClose, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [partner, setPartner] = useState({ name: partnerName, profile_image: partnerImage });
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/${partnerId}?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) { setMessages(data.messages || []); if (data.partner) setPartner(prev => ({ ...prev, ...data.partner })); }
    } catch (err) { console.error('Fetch messages error:', err); }
    finally { setLoadingMsgs(false); }
  }, [partnerId]);

  useEffect(() => { setLoadingMsgs(true); setMessages([]); fetchMessages(); pollRef.current = setInterval(fetchMessages, 5000); return () => clearInterval(pollRef.current); }, [fetchMessages]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ receiver_id: partnerId, content: text }) });
      if (res.ok) { setNewMessage(''); await fetchMessages(); } else { toast({ title: 'Send failed', variant: 'destructive' }); }
    } catch { toast({ title: 'Error', description: 'Network error sending message.', variant: 'destructive' }); }
    finally { setSending(false); }
  };

  const formatTime = (dateStr) => { const d = new Date(dateStr); const now = new Date(); if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); };

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      <div className="flex items-center gap-3 p-3 bg-[#0F172A] border-b-2 border-black flex-shrink-0">
        <button onClick={onClose} className="text-neutral-400 hover:text-white transition-colors" data-testid="chat-close-btn"><X size={20} weight="bold" /></button>
        <div className="w-8 h-8 bg-[#2563EB] border border-white/30 flex items-center justify-center overflow-hidden flex-shrink-0 rounded-sm">
          {partner.profile_image ? <img src={partner.profile_image} alt="" className="w-full h-full object-cover" /> : <Briefcase size={16} weight="bold" className="text-white" />}
        </div>
        <p className="text-white font-bold text-sm truncate">{partner.name || 'Contractor'}</p>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-neutral-100 dark:bg-neutral-900/80">
        {loadingMsgs ? <div className="flex items-center justify-center h-full"><ArrowsClockwise size={24} weight="bold" className="animate-spin text-[#2B4C6F]" /></div>
         : messages.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-center px-4"><ChatCircle size={40} weight="bold" className="text-neutral-300 dark:text-neutral-600 mb-2" /><p className="text-sm text-neutral-500 font-bold">No messages yet</p></div>
         : messages.map((msg) => { const isMine = msg.sender_id === currentUserId; return (
          <div key={msg._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 text-sm ${isMine ? 'bg-[#2563EB] text-[#0F172A] border border-[#203A55]' : 'bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white border border-neutral-300 dark:border-neutral-700'}`}>
              <p className="break-words leading-relaxed">{msg.content}</p>
              <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-neutral-400'}`}>{formatTime(msg.created_at)}</p>
            </div>
          </div>); })}
      </div>
      <form onSubmit={handleSend} className="flex items-center gap-2 p-3 bg-white dark:bg-[#0F172A] border-t-2 border-black flex-shrink-0">
        <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-1 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-[#2B4C6F]" data-testid="chat-input" />
        <button type="submit" disabled={!newMessage.trim() || sending} className="h-10 w-10 flex items-center justify-center bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] border-2 border-black shadow-[2px_2px_0px_#000] disabled:opacity-40 transition-all flex-shrink-0" data-testid="chat-send-btn"><PaperPlaneTilt size={18} weight="bold" /></button>
      </form>
    </div>
  );
}

export function ConversationsList({ onSelectConversation, currentUserId }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const data = await res.json(); if (data.success) setConversations(data.conversations || []); }
      } catch (err) { console.error('Conversations fetch error:', err); }
      finally { setLoading(false); }
    };
    fetchConversations();
    const interval = setInterval(fetchConversations, 8000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (dateStr) => { if (!dateStr) return ''; const d = new Date(dateStr); const now = new Date(); if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); return d.toLocaleDateString([], { month: 'short', day: 'numeric' }); };

  if (loading) return <div className="flex items-center justify-center h-full"><ArrowsClockwise size={24} weight="bold" className="animate-spin text-[#2B4C6F]" /></div>;
  if (conversations.length === 0) return <div className="flex flex-col items-center justify-center h-full text-center px-4"><ChatCircle size={40} weight="bold" className="text-neutral-300 dark:text-neutral-600 mb-2" /><p className="text-sm text-neutral-500 font-bold">No conversations yet</p></div>;

  return (
    <div className="overflow-y-auto h-full" data-testid="conversations-list">
      {conversations.map((conv) => {
        const p = conv.partner; const lastMsg = conv.last_message; const unread = conv.unread_count || 0;
        return (
          <button key={p._id} onClick={() => onSelectConversation(p._id, p.name, p.profile_image)} className="w-full flex items-center gap-3 p-3 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-left border-b border-neutral-200 dark:border-neutral-700" data-testid={`conversation-${p._id}`}>
            <div className="w-10 h-10 bg-[#2563EB] border-2 border-black flex items-center justify-center overflow-hidden flex-shrink-0">{p.profile_image ? <img src={p.profile_image} alt="" className="w-full h-full object-cover" /> : <Briefcase size={18} weight="bold" className="text-white" />}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between"><p className="font-bold text-neutral-900 dark:text-white text-sm truncate">{p.name}</p>{lastMsg && <span className="text-[10px] text-neutral-400 flex-shrink-0 ml-2">{formatTime(lastMsg.created_at)}</span>}</div>
              {lastMsg && <p className="text-xs text-neutral-500 truncate mt-0.5">{lastMsg.sender_id === currentUserId ? 'You: ' : ''}{lastMsg.content}</p>}
            </div>
            {unread > 0 && <span className="w-5 h-5 bg-[#3B82F6] text-[#0F172A] text-[10px] font-bold flex items-center justify-center flex-shrink-0 border border-black">{unread > 9 ? '9+' : unread}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function MessagingSidebar({ isOpen, onClose, activeChatPartner, onOpenChat, currentUserId }) {
  const [view, setView] = useState(activeChatPartner ? 'chat' : 'list');
  const [chatPartner, setChatPartner] = useState(activeChatPartner);

  useEffect(() => { if (activeChatPartner) { setChatPartner(activeChatPartner); setView('chat'); } }, [activeChatPartner]);

  const handleSelectConversation = (id, name, image) => { setChatPartner({ id, name, image }); setView('chat'); };
  const handleBackToList = () => { setView('list'); setChatPartner(null); };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="messaging-sidebar">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-[#1A1A1A] border-l-4 border-black shadow-[-8px_0_20px_rgba(0,0,0,0.3)] flex flex-col h-full animate-slide-in">
        <div className="flex items-center justify-between p-3 bg-[#2563EB] border-b-2 border-black flex-shrink-0">
          <div className="flex items-center gap-2">
            {view === 'chat' && <button onClick={handleBackToList} className="text-white/80 hover:text-white transition-colors" data-testid="chat-back-btn"><ArrowLeft size={20} weight="bold" /></button>}
            <ChatCircleDots size={22} weight="bold" className="text-white" />
            <h3 className="text-white font-black uppercase text-sm tracking-wider">{view === 'chat' ? 'Chat' : 'Messages'}</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors" data-testid="sidebar-close-btn"><X size={20} weight="bold" /></button>
        </div>
        <div className="flex-1 overflow-hidden">
          {view === 'chat' && chatPartner ? (
            <ChatPanel partnerId={chatPartner.id} partnerName={chatPartner.name} partnerImage={chatPartner.image} onClose={handleBackToList} currentUserId={currentUserId} />
          ) : (
            <ConversationsList onSelectConversation={handleSelectConversation} currentUserId={currentUserId} />
          )}
        </div>
      </div>
    </div>
  );
}
